import {
  useGetActivationQuery,
  useGetActivityMetricsQuery,
} from 'api/recoveryAi'
import type { ActivationSummary } from 'api/recoveryAi.types'
import { PROMO_CREDIT_CENTS } from './constants'

// Composes the WP7 ActivationSummary view-side from the live activity metrics +
// activation provenance + interim constants. The billing/credit backend does not
// exist yet, so those two fields are either an interim constant
// (availableCreditCents) or a graceful `null` — NEVER a fabricated 0 or date. The
// consumer (ActivationSuccess) renders an em-dash for the nulls and overlays the
// pricing-modal router-state hand-off where one exists.
//
// `isLoading` merges both queries so the success page can distinguish "still in
// flight" (a cold-cache direct visit/refresh) from "backend genuinely returned
// null" — the former should not flash an em-dash on the celebratory page.
export const useActivationSummary = (): ActivationSummary & {
  isLoading: boolean
} => {
  const { data: metrics, isLoading: isMetricsLoading } =
    useGetActivityMetricsQuery()
  const { data: activation, isLoading: isActivationLoading } =
    useGetActivationQuery()

  return {
    isLoading: isMetricsLoading || isActivationLoading,
    // Sourced from the authoritative GET recovery/configuration/activation
    // (AI-1008) — the enabling ActivationEvent's created_at, never the client
    // clock. `null` (em-dash) until that event exists.
    activatedAt: activation?.activatedAt ?? null,
    // Provisional N. TODO(AI-208): M-scope (merchant_visible metrics vs .live ROI)
    // is unreconciled — do not lock the "N of M" count semantics on these.
    locationsActive: metrics?.activeLocationsCount ?? null,
    locationsTotal: metrics?.totalLocationsCount ?? null,
    availableCreditCents: PROMO_CREDIT_CENTS,
    // Default until the billing backend records the merchant's chosen method. AI-1133:
    // the choice is hidden and everyone bills per-location, so a direct visit/refresh
    // to the success page (no router-state hand-off) shows the per-location default.
    billingMethod: 'per_location',
    // Authorizer is an audit fact, not the current viewer — sourced from the same
    // activation event as activatedAt. `null` → em-dash; NOT
    // selectCurrentMerchantUser (that renders whoever is viewing on any
    // refresh/direct visit).
    authorizedByName: activation?.authorizedByName ?? null,
    authorizedByEmail: activation?.authorizedByEmail ?? null,
  }
}
